import { Component, Input } from '@angular/core';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { Subject, auditTime, filter, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import * as echarts from 'echarts';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-horizontal-grouped-bar-chart',
  imports: [DataErrorComponent, CommonModule],
  templateUrl: './horizontal-grouped-bar-chart.component.html',
  styleUrl: './horizontal-grouped-bar-chart.component.scss',
})
export class HorizontalGroupedBarChartComponent {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  public hasError = false;
  public errorMessage = '';
  private unSubscribe$: Subject<void> = new Subject();
  private chartData: any = null;

  constructor(
    private configService: ConfigService,
    private filterState: GlobalService
  ) { }

  private destroy$ = new Subject<void>();

  ngAfterViewInit() {
    this.filterState.filters$
      .pipe(
        // short delay to collect rapid emissions
        auditTime(1000),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        if (filters?.widgetId === this.data.widgetId) return;
	setTimeout(() => this.loadAndRenderChart(filters?.filter ?? {}), 0);
      });
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }
    this.myChart.showLoading();
    this.renderChart(filters);
    // this.configService.getApi(this.data.apiUrl, this.data.functionName, filters, this.data)
    //   .pipe(takeUntil(this.unSubscribe$))
    //   .subscribe({
    //     next: (res) => {
    //       this.chartData = this.data?.responseKey ? res[0][this.data.responseKey] : res;
    //       // Handle empty or invalid data
    //       const isEmpty =
    //         !this.chartData ||
    //         !this.chartData.categories?.length;

    //       if (isEmpty) {
    //         this.hasError = true;
    //         this.errorMessage = 'No data found for the selected filters.';
    //         this.myChart.clear();
    //       } else {
    //         this.hasError = false;
    //         this.renderChart(this.chartData);
    //       }

    //       this.myChart.hideLoading();
    //       window.addEventListener('resize', this.resizeChart);
    //     },
    //     error: (err) => {
    //       this.hasError = true;
    //       this.errorMessage = 'No Data available for the chart.';
    //       this.myChart?.clear();
    //       this.myChart.hideLoading();
    //     }
    //   });
  }

  private renderChart(filters?: any): void {
    this.myChart.hideLoading();

    const rawdata: any = this.data.rawData;
    const selectedModels = Array.isArray(filters?.final_em)
      ? filters.final_em
      : [];
    const selectedTypes = Array.isArray(filters?.product_portfolio)
      ? filters.product_portfolio
      : [];
    const data = rawdata.filter((item: any) => {
      const modelMatches =
        selectedModels.includes('All') || selectedModels.length === 0
          ? true
          : selectedModels.includes(item.model);

      const typeMatches =
        selectedTypes.includes('All') || selectedTypes.length === 0
          ? true
          : selectedTypes.includes(item.type);

      return modelMatches && typeMatches;
    });
    if (data.length == 0) {
      this.hasError = true
      this.errorMessage = 'No Data available for the chart.';
    }
    else {
      this.hasError = false
    }

    //single graph
    // const createPlannedSeries = (metric: string, xAxisIndex: number, yAxisIndex: number, gridIndex: number) => ({
    //   name: `Planned`, // Same name for legend grouping
    //   type: "custom",
    //   xAxisIndex,
    //   yAxisIndex,
    //   gridIndex,
    //   renderItem: (params: any, api: any) => {
    //     const yIndex = api.value(0);
    //     const plannedValue = api.value(1);
    //     const actualValue = api.value(2);

    //     const coordPlanned = api.coord([plannedValue, yIndex]);
    //     const coordActual = api.coord([actualValue, yIndex]);

    //     const plannedLineColor = "#444343cc"; // dark red

    //     // Offset so label doesn’t overlap bar
    //     const labelOffsetX = plannedValue > actualValue ? 5 : -25;

    //     return {
    //       type: "group",
    //       children: [
    //         // Dotted line from actual bar end to planned value
    //         {
    //           type: "line",
    //           shape: {
    //             x1: coordActual[0],
    //             y1: coordActual[1],
    //             x2: coordPlanned[0],
    //             y2: coordPlanned[1],
    //           },
    //           style: {
    //             stroke: plannedLineColor,
    //             lineWidth: 2,
    //             lineDash: [4, 4],
    //           },
    //         },
    //         // Label showing planned value
    //         {
    //           type: "text",
    //           style: {
    //             text: `${plannedValue}%`,
    //             x: coordPlanned[0] + labelOffsetX,
    //             y: coordPlanned[1] - 5,
    //             fill: plannedLineColor,
    //             font: "12px sans-serif",
    //           },
    //         },
    //       ],
    //     };
    //   },
    //   data: data.map((d: any, i) => [i, d.planned[metric], d.actual[metric]]),
    //   silent: true,
    // });

    const createPlannedSeries = (
      metric: string,
      xAxisIndex: number,
      yAxisIndex: number,
      gridIndex: number
    ) => ({
      name: `Planned`,
      type: 'custom',
      xAxisIndex,
      yAxisIndex,
      gridIndex,
      z: 1000,
      renderItem: (params: any, api: any) => {
        const yIndex = api.value(0);
        const plannedValue = api.value(1);
        const actualValue = api.value(2);

        const coordPlanned = api.coord([plannedValue, yIndex]);

        const barWidth = 20; // Must match barWidth in actual series
        const barCenterOffset = barWidth / 2;

        const plannedLineColor = '#444343cc';

        const labelOffsetX = plannedValue > actualValue ? 5 : -25;

        return {
          type: 'group',
          children: [
            // Vertical dotted line
            {
              type: 'line',
              shape: {
                x1: coordPlanned[0],
                y1: coordPlanned[1] - barCenterOffset,
                x2: coordPlanned[0],
                y2: coordPlanned[1] + barCenterOffset,
              },
              style: {
                stroke: plannedLineColor,
                lineWidth: 2,
                lineDash: [4, 4],
              },
              z: 1000,
            },
            // Label
            {
              type: 'text',
              style: {
                text: `${plannedValue}%`,
                x: coordPlanned[0] + 5, // offset slightly to avoid overlap
                y: coordPlanned[1], // center vertically
                fill: plannedLineColor,
                font: '12px sans-serif',
                align: 'left', // ensures horizontal alignment
                verticalAlign: 'middle', // ensures vertical centering
              },
              z: 1000,
            },
          ],
        };
      },
      data: data.map((d: any, i: any) => [i, d.planned[metric], d.actual[metric]]),
      silent: true,
    });
    // Compute dynamic grid height based on number of Y-axis items
    const rowHeight = 40; // pixels per Y-axis interval (adjust as desired)
    const numRows = data.length;
    const gridHeight = numRows * rowHeight;

    // Also resize the chart container to fit (optional but recommended)
    const chartContainer = document.getElementById('chart');
    if (chartContainer) {
      chartContainer.style.height = `${gridHeight + 150}px`; // +150 for padding/titles
    }
    const option = {
      grid: [
        { left: '5%', top: 50, width: '22%', height: gridHeight }, // CT
        { left: '28%', top: 50, width: '22%', height: gridHeight }, // Sourcing
        { left: '51%', top: 50, width: '22%', height: gridHeight }, // Quality
        { left: '74%', top: 50, width: '22%', height: gridHeight }, // Mfg
      ],
      title: [
        {
          text: 'Customer Tailoring',
          left: '16%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 18, fontWeight: 'bold' },
        },
        {
          text: 'Sourcing',
          left: '39%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 18, fontWeight: 'bold' },
        },
        {
          text: 'Quality',
          left: '62%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 18, fontWeight: 'bold' },
        },
        {
          text: 'Manufacturing',
          left: '85%',
          top: -5,
          textAlign: 'center',
          textStyle: { fontSize: 18, fontWeight: 'bold' },
        },
      ],
      xAxis: [
        {
          type: 'value',
          name: 'CT',
          gridIndex: 0,
          max: 100,
          show: false,
        },
        {
          type: 'value',
          name: 'Sourcing',
          gridIndex: 1,
          max: 100,
          show: false,
        },
        { type: 'value', name: 'Quality', gridIndex: 2, max: 100, show: false },
        { type: 'value', name: 'Mfg', gridIndex: 3, max: 100, show: false },
      ],
      yAxis: [
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 0,
          show: true, // must be true for split lines
          axisTick: { show: false }, // hide ticks
          axisLabel: {
            show: true,
            fontWeight: 'bold',
            fontSize: 14,
            color: '#000',
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 1,
          show: true,
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 2,
          show: true,
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
        {
          type: 'category',
          data: data.map((d: { model: any; }) => d.model),
          gridIndex: 3,
          show: true,
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#ccc',
              width: 1,
              type: 'solid',
            },
          },
        },
      ],
      series: [
        {
          name: 'CT Actual',
          type: 'bar',
          data: this.buildAdaptiveActualData(
            data.map((d: { actual: { ct: any } }) => d.actual.ct)
          ),
          xAxisIndex: 0,
          yAxisIndex: 0,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
          barCategoryGap: 20,
        },
        {
          name: 'Sourcing Actual',
          type: 'bar',
          data: this.buildAdaptiveActualData(
            data.map((d: { actual: { sourcing: any } }) => d.actual.sourcing)
          ),
          xAxisIndex: 1,
          yAxisIndex: 1,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
        },
        {
          name: 'Quality Actual',
          type: 'bar',
          data: this.buildAdaptiveActualData(
            data.map((d: { actual: { quality: any } }) => d.actual.quality)
          ),
          xAxisIndex: 2,
          yAxisIndex: 2,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
        },
        {
          name: 'Mfg Actual',
          type: 'bar',
          data: this.buildAdaptiveActualData(
            data.map((d: { actual: { mfg: any } }) => d.actual.mfg)
          ),
          xAxisIndex: 3,
          yAxisIndex: 3,
          itemStyle: {
            color: (params: any) =>
              this.typeColors[data[params.dataIndex].type] || '#3BA272',
          },
          barWidth: 20,
        },

        { ...createPlannedSeries('ct', 0, 0, 0) },
        { ...createPlannedSeries('sourcing', 1, 1, 1) },
        { ...createPlannedSeries('quality', 2, 2, 2) },
        { ...createPlannedSeries('mfg', 3, 3, 3) },
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const dataIndex = params[0].dataIndex;
          const gridIndex = params[0].axisIndex;
          const d: any = data[dataIndex];

          const metricMap = ['ct', 'sourcing', 'quality', 'mfg'];
          const metric = metricMap[gridIndex] || 'ct';
          const spendMn = Number(d?.totalSpend ?? 0);
          const spendDisplay = Number.isFinite(spendMn)
            ? spendMn.toFixed(2)
            : '0.00';
          const actualPct = Number(d?.actual?.[metric] ?? 0);
          const plannedPct = Number(d?.planned?.[metric] ?? 0);
          const actualValueMn = (actualPct / 100) * spendMn;
          const actualValueDecimals =
            Number.isFinite(actualValueMn) && Math.abs(actualValueMn) < 0.1 ? 4 : 2;
          const actualValueDisplay = Number.isFinite(actualValueMn)
            ? actualValueMn.toFixed(actualValueDecimals)
            : '0.00';

          return `
      <b>${d.model}</b><br/>
      Actual - ${actualPct.toFixed(2)}% - ${actualValueDisplay} M<br/>
      Planned: ${plannedPct.toFixed(2)}%
    `;
        },
      },
    };

    this.myChart.setOption(option);
  }

  private buildAdaptiveActualData(values: any[]): any[] {
    return values.map((rawValue: any) => {
      const value = Number(rawValue);
      const isSmall = Number.isFinite(value) && value < 0.1;

      return {
        value: Number.isFinite(value) ? value : 0,
        label: {
          show: true,
          position: isSmall ? 'top' : 'inside',
          color: isSmall ? '#222' : '#fff',
          distance: isSmall ? 4 : 0,
          formatter: (params: any) => `${params.value}%`,
        },
      };
    });
  }

  private resizeChart = () => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.myChart?.dispose();
    window.removeEventListener('resize', this.resizeChart);
    this.destroy$.next();
    this.destroy$.complete();
  }

  typeColors: Record<string, string> = {
    'Growth': '#98d34f', // Green
    'Core/Niche': '#7eccfaff', // Blue
    'Harvest/Transition': '#d6d6d6', // Orange
  };

  typeKeys(): string[] {
    return Object.keys(this.typeColors);
  }

}
